import { experimental_createQueryPersister } from '@tanstack/query-persist-client-core'
import { del, get, set } from 'idb-keyval'
import { cryptoManager, EncryptedData } from '@/utils/crypto'

interface SecurePersisterOptions {
    maxAge?: number
    buster?: string
    prefix?: string
}

export function createSecureQueryPersister(options: SecurePersisterOptions = {}) {
    const { maxAge = 1000 * 60 * 60 * 24 * 365, buster = '' } = options

    return experimental_createQueryPersister({
        storage: {
            getItem: async (key: string): Promise<string | null> => {
                try {
                    const encryptedData = (await get(key)) as EncryptedData

                    if (!encryptedData) return null

                    const decryptedData = await cryptoManager.decrypt(encryptedData)
                    const parsed = JSON.parse(decryptedData)

                    // Check if data is expired
                    if (parsed.state.dataUpdatedAt + maxAge < Date.now()) {
                        await del(key)
                        return null
                    }

                    return decryptedData
                } catch (error) {
                    //console.error('Failed to decrypt query cache:', error)
                    // If decryption fails, remove the corrupted data
                    await del(key)
                    return null
                }
            },

            setItem: async (key: string, value: string): Promise<void> => {
                try {
                    const encryptedData = await cryptoManager.encrypt(value)

                    await set(key, encryptedData)
                } catch (error) {
                    console.error('Failed to encrypt and store query cache:', error)
                    throw error
                }
            },

            removeItem: async (key: string): Promise<void> => {
                await del(key)
            },
        },
        buster,
        maxAge,
    }).persisterFn
}
